import React, { FC } from 'react'

import { EuiPanel, EuiTitle, EuiText, EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiButtonIcon } from '@elastic/eui'

import { AIPlan, AIPlanCustomSection } from 'api/interfaces/AIPlan'

import AIPlanConnectedTvNetworks from './AIPlanConnectedTvNetworks'
import AIPlanTopInternetDisplaySites from './AIPlanTopInternetDisplaySites'

export interface AIPlanPlacementChartsProps {
  aiPlan: AIPlan
  hasInternet: boolean
  hasTv: boolean
  whereAdsAppearTitle: string
  auctionBasedPlacementsSection?: AIPlanCustomSection
  ctvSection?: AIPlanCustomSection
  internetSection?: AIPlanCustomSection
  editMode?: boolean
  onEditWhereAdsAppearTitle?: () => void
  onEditAuctionBasedPlacements?: () => void
  onEditCtvPlacementCategories?: () => void
  onEditInternetAdPlacements?: () => void
}

const AIPlanPlacementCharts: FC<AIPlanPlacementChartsProps> = ({ aiPlan, hasInternet, hasTv, whereAdsAppearTitle, auctionBasedPlacementsSection, ctvSection, internetSection, editMode, onEditWhereAdsAppearTitle, onEditAuctionBasedPlacements, onEditCtvPlacementCategories, onEditInternetAdPlacements }) => {
  const auctionTitle = auctionBasedPlacementsSection?.title ?? 'Auction-Based Placements'
  const auctionContent = auctionBasedPlacementsSection?.content ?? 'Ad placements are made via auction-based ad exchanges. Bidding is optimized to prioritize audience and geographic accuracy over publisher allocation, so some variation in final distribution may occur.'
  const auctionShowTitle = auctionBasedPlacementsSection?.showTitle ?? true

  const ctvTitle = ctvSection?.title ?? 'CTV: Placement Categories'
  const ctvContent = ctvSection?.content ?? 'Based on chosen audience and historic delivery data, CTV impressions are projected to appear across premium outlets in the following categories. Precise distribution may vary based on live auction constraints.'
  const ctvShowTitle = ctvSection?.showTitle ?? true

  const internetTitle = internetSection?.title ?? 'Internet Ad Placements'
  const internetContent = internetSection?.content ?? 'Internet ads will be placed across any of the following sites, optimized for audience match.'
  const internetShowTitle = internetSection?.showTitle ?? true

  const width = hasInternet && hasTv ? '45%' : '90%'

  return (
    <EuiPanel paddingSize='l'>
      <EuiFlexGroup alignItems='center' gutterSize='s'>
        <EuiFlexItem grow={false}>
          <EuiTitle size='m'>
            <h2>{whereAdsAppearTitle}</h2>
          </EuiTitle>
        </EuiFlexItem>
        {editMode && onEditWhereAdsAppearTitle && (
          <EuiFlexItem grow={false}>
            <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEditWhereAdsAppearTitle} aria-label='Edit Where Ads Appear title' />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      <EuiSpacer size='l' />

      {auctionBasedPlacementsSection?.hideAll !== true && (
        <div style={{ marginLeft: '2rem' }}>
          <EuiFlexGroup alignItems='center' gutterSize='s'>
            {auctionShowTitle && (
              <EuiFlexItem grow={false}>
                <EuiTitle size='xs'>
                  <h3>{auctionTitle}</h3>
                </EuiTitle>
              </EuiFlexItem>
            )}
            {editMode && onEditAuctionBasedPlacements && (
              <EuiFlexItem grow={false}>
                <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEditAuctionBasedPlacements} aria-label='Edit Auction-Based Placements section' />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>

          <EuiSpacer size='s' />

          <EuiText size='m' style={{ whiteSpace: 'pre-wrap' }}>
            {auctionContent}
          </EuiText>
        </div>
      )}

      <EuiSpacer size='l' />

      <EuiFlexGroup gutterSize='xl'>
        {hasTv && (
          <EuiFlexItem style={{ width: width }}>
            {ctvSection?.hideAll !== true && (
              <>
                <EuiFlexGroup alignItems='center' gutterSize='s'>
                  {ctvShowTitle && (
                    <EuiFlexItem grow={false}>
                      <EuiTitle size='xs'>
                        <h3>{ctvTitle}</h3>
                      </EuiTitle>
                    </EuiFlexItem>
                  )}
                  {editMode && onEditCtvPlacementCategories && (
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEditCtvPlacementCategories} aria-label='Edit CTV Placement Categories section' />
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
                {ctvShowTitle && <EuiSpacer size='s' />}
                <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
                  {ctvContent}
                </EuiText>
                <EuiSpacer size='m' />
              </>
            )}
            <AIPlanConnectedTvNetworks aiPlan={aiPlan} />
          </EuiFlexItem>
        )}

        {hasInternet && (
          <EuiFlexItem style={{ width: width }}>
            {internetSection?.hideAll !== true && (
              <>
                <EuiFlexGroup alignItems='center' gutterSize='s'>
                  {internetShowTitle && (
                    <EuiFlexItem grow={false}>
                      <EuiTitle size='xs'>
                        <h3>{internetTitle}</h3>
                      </EuiTitle>
                    </EuiFlexItem>
                  )}
                  {editMode && onEditInternetAdPlacements && (
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEditInternetAdPlacements} aria-label='Edit Internet Ad Placements section' />
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
                {internetShowTitle && <EuiSpacer size='s' />}
                <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
                  {internetContent}
                </EuiText>
                <EuiSpacer size='m' />
              </>
            )}
            <AIPlanTopInternetDisplaySites aiPlan={aiPlan} />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </EuiPanel>
  )
}

export default AIPlanPlacementCharts
